import classNames from "classnames";
import React, { MouseEventHandler, useCallback, useEffect, useMemo } from "react";
import { isValidNumber } from "../../utils/number-utils";
import { IconButton } from "../button";
import { IconCancel } from "../icons";
import { Message } from "../message/message";
import { Panel, PanelProps } from "../panel/panel";
import { CSS_CLASSES } from "./notifications-css-classes";
import { NotificationProps } from "./notifications.types";

/**
 * The `Notification` component can be used to communicate important messages to the user in a visual way that draws
 * user attention to them.
 *
 * It has two variants to be used for different UX intent:
 *
 * - **inline** : To be used inline within page content to communicate context sensitive, persistent messages.
 * - **floating** : To be used for transient feedback messages that are associated with wider-scope, asynchronous user
 * action. These are dismissible and can optionally be set up to auto close after a customizable time interval. This
 * variant is used by the `Notifications` component.
 */
export const Notification = ({
  id,
  variant = "inline",
  onClose,
  autoCloseDelay = 0,
  showCloseButton = variant === "inline" ? false : true,
  actions,
  status = "info",
  size = "medium",
  showStatusLabel = true,
  matchColor = false,
  icon,
  inline = true,
  className,
  children,
  ...restProps
}: NotificationProps) => {
  const handleCloseButtonClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      onClose?.(event, id);
    },
    [id, onClose]
  );

  useEffect(() => {
    let timer: number = undefined;
    if (isValidNumber(autoCloseDelay) && autoCloseDelay > 0) {
      timer = window.setTimeout(handleCloseButtonClick, autoCloseDelay);
    }
    return () => {
      timer && window.clearTimeout(timer);
    };
  }, [onClose, autoCloseDelay, handleCloseButtonClick]);

  const panelProps: PanelProps = useMemo(
    () => ({
      paddingY: "md",
      paddingX: "xl",
      border: "0",
      rounded: "none",
    }),
    []
  );

  const panelClassName = useMemo(
    () =>
      classNames(
        CSS_CLASSES.NOTIFICATION_ROOT,
        {
          [CSS_CLASSES.NOTIFICATION_FLOATING_ROOT]: variant === "floating",
          [CSS_CLASSES.NOTIFICATION_INLINE_ROOT]: variant === "inline",
          [CSS_CLASSES.NOTIFICATION_INFO]: status === "info",
          [CSS_CLASSES.NOTIFICATION_SUCCESS]: status === "success",
          [CSS_CLASSES.NOTIFICATION_ERROR]: status === "error",
          [CSS_CLASSES.NOTIFICATION_WARNING]: status === "warning",
          [CSS_CLASSES.NOTIFICATION_WITH_CLOSE_BUTTON]: showCloseButton,
        },
        className
      ),
    [className, showCloseButton, variant, status]
  );

  const maybeCloseButton = useMemo(() => {
    const closeButtonClassName = classNames(CSS_CLASSES.NOTIFICATION_CLOSE_BUTTON, {
      [CSS_CLASSES.NOTIFICATION_SMALL_CLOSE_BUTTON]: size === "small",
      [CSS_CLASSES.NOTIFICATION_MEDIUM_CLOSE_BUTTON]: size === "medium",
      [CSS_CLASSES.NOTIFICATION_LARGE_CLOSE_BUTTON]: size === "large",
    });

    return showCloseButton ? (
      <IconButton
        onClick={handleCloseButtonClick}
        className={closeButtonClassName}
        label="Close"
        variant="ghost"
        color="gray"
        size="small"
        icon={<IconCancel />}
      />
    ) : null;
  }, [handleCloseButtonClick, showCloseButton, size]);

  const maybeActions = useMemo(
    () =>
      actions ? (
        <div
          className={classNames({
            [CSS_CLASSES.NOTIFICATION_SMALL_ACTIONS]: size === "small",
            [CSS_CLASSES.NOTIFICATION_MEDIUM_ACTIONS]: size === "medium",
            [CSS_CLASSES.NOTIFICATION_LARGE_ACTIONS]: size === "large",
          })}
        >
          {actions}
        </div>
      ) : null,
    [actions, size]
  );

  return (
    <Panel id={id} {...panelProps} className={panelClassName} {...restProps}>
      {maybeCloseButton}
      <div className={CSS_CLASSES.NOTIFICATION_CONTENT}>
        <Message
          status={status}
          size={size}
          showStatusLabel={showStatusLabel}
          matchColor={matchColor}
          icon={icon}
          inline={inline}
        >
          {children}
        </Message>
        {maybeActions}
      </div>
    </Panel>
  );
};
